'use client';

import React from 'react';
import { formatAddress } from '@/utils/formatAddress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import VerticalTable from '../VerticalTable';
import { faScroll } from '@fortawesome/pro-duotone-svg-icons';
import { RoundedSectionCard } from '@/components/common/RoundedSectionCard';
import CpvCodeButton from './CpvCodeButton';
import { TenderDetails } from '@/actions/dtos/tender/tender';
import { ProcedureType, ProcurementActivity, ProcurementScope, Regulation } from '@/actions/dtos/tender/procurement';
import Link from 'next/link';
import { getSourcePath } from '@/rewrites';
import { useAccount } from '@/hooks/useAccount'; // Import the account hook
import { SubscriptionTier } from '@/actions/dtos/subscription';

interface ProcurementDetailsSectionProps {
  tenderDetails: TenderDetails;
  googleMapsApiKey?: string;
  isAuthenticated: boolean;
}

const renderLinkOrText = (item: { name: string; slug?: string }) => {
  return item.slug ? (
    <Link
      href={getSourcePath('/glossary') + `/${item.slug}`}
      target="_blank"
      className="underline decoration-[#D9D9D9] decoration-dotted underline-offset-4"
    >
      {item.name}
    </Link>
  ) : (
    <span>{item.name}</span>
  );
};

const ProcurementDetailsSection: React.FC<ProcurementDetailsSectionProps> = ({
  tenderDetails,
  googleMapsApiKey,
  isAuthenticated,
}) => {
  const { executionSite } = tenderDetails.procurement;
  const { currentTier, customer } = useAccount();

  const hasGeoLocation = (site: any): site is { geoLocation: { latitude: number; longitude: number } } => {
    return site && site.geoLocation && site.geoLocation.latitude && site.geoLocation.longitude;
  };

  const mapSrc = hasGeoLocation(executionSite)
    ? `https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${executionSite.geoLocation.latitude},${executionSite.geoLocation.longitude}&zoom=9&region=de`
    : undefined;

  const hasAccountData = customer && currentTier !== undefined;
  const isPremiumUser = hasAccountData && isAuthenticated && currentTier !== SubscriptionTier.BASIC;

  const details = [
    ...(isPremiumUser && tenderDetails.publication?.title
      ? [
          {
            label: 'Titel:',
            data: tenderDetails.publication.title,
          },
        ]
      : []),
    {
      label: 'Reichweite:',
      data: ProcurementScope[tenderDetails.procurement.scope as unknown as keyof typeof ProcurementScope] || null,
    },
    {
      label: 'Vergabeart:',
      data: tenderDetails.procurement.procedure
        ? renderLinkOrText(ProcedureType[tenderDetails.procurement.procedure as keyof typeof ProcedureType])
        : null,
    },
    {
      label: 'Vergabeverordnung:',
      data: tenderDetails.procurement.regulation
        ? renderLinkOrText(Regulation[tenderDetails.procurement.regulation as keyof typeof Regulation])
        : null,
    },
    {
      label: 'Leistung:',
      data:
        ProcurementActivity[tenderDetails.procurement.activity as unknown as keyof typeof ProcurementActivity] || null,
    },
    {
      label: 'Klassifizierung:',
      data:
        tenderDetails.procurement.cpvCodes && tenderDetails.procurement.cpvCodes.length > 0
          ? tenderDetails.procurement.cpvCodes.map((code) => <CpvCodeButton key={code.identifier} code={code} />)
          : null,
    },
    {
      label: 'Zuschlagskriterien:',
      data: tenderDetails.procurement.awardingCriteria ? tenderDetails.procurement.awardingCriteria.join(', ') : null,
    },
    {
      label: 'Auftrags-Budget:',
      data: tenderDetails.procurement.officialBudget
        ? Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 0,
          }).format(tenderDetails.procurement.officialBudget)
        : null,
    },
    {
      label: 'Erfüllungsort:',
      data: formatAddress(tenderDetails.procurement.executionSite),
    },
  ];

  return (
    <RoundedSectionCard>
      <div className="flex flex-col lg:flex-row">
        <div className={hasGeoLocation(executionSite) ? 'mb-4 lg:mb-0 lg:mr-4 lg:w-1/2' : 'w-full'}>
          <VerticalTable
            title={
              <div className="mb-5 flex gap-3">
                <span className="flex h-8 w-6 items-center justify-center">
                  <FontAwesomeIcon className="fa-fw h-6 w-6" icon={faScroll} />
                </span>
                <h2 className="text-xl font-light">AUSSCHREIBUNG</h2>
              </div>
            }
            details={details}
          />
        </div>
        {hasGeoLocation(executionSite) && (
          <div className="w-full lg:flex lg:w-1/2 lg:justify-end">
            <iframe
              src={mapSrc}
              width="100%"
              style={{ border: 0 }}
              allowFullScreen={false}
              loading="lazy"
              title="Erfüllungsort"
              className="h-60 rounded lg:h-full"
            ></iframe>
          </div>
        )}
      </div>
    </RoundedSectionCard>
  );
};

export default ProcurementDetailsSection;
